import { useEffect, useState } from 'react';
import { formatBytes, getOfflineDiagnostics, prepareOfflineDevice, type OfflineDiagnostics } from './offline';
import './offline-check.css';

function StatusRow({ label, ok, detail }: { label: string; ok: boolean | null; detail: string }) {
  return (
    <div className="offline-status-row">
      <span className={`offline-dot ${ok === true ? 'ok' : ok === false ? 'bad' : 'neutral'}`}>{ok === true ? '✓' : ok === false ? '!' : '•'}</span>
      <div><strong>{label}</strong><small>{detail}</small></div>
    </div>
  );
}

export default function OfflineCheck() {
  const [state, setState] = useState<OfflineDiagnostics | null>(null);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('Comprobando este dispositivo…');

  async function refresh() {
    try {
      setState(await getOfflineDiagnostics());
      setMessage('Diagnóstico actualizado.');
    } catch {
      setMessage('No fue posible completar todas las comprobaciones.');
    }
  }

  async function prepare() {
    setWorking(true);
    setMessage('Preparando caché, Wallet cifrado y almacenamiento local…');
    try {
      setState(await prepareOfflineDevice());
      setMessage('Preparación terminada. Ya puedes hacer la prueba en modo avión.');
    } catch {
      setMessage('La preparación terminó con una advertencia. Revisa los indicadores.');
    } finally {
      setWorking(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  const ready = Boolean(state?.serviceWorkerControlled && state?.shellCached && state?.walletCached);

  return (
    <main className="offline-check-page">
      <header className="offline-check-header">
        <div><strong>EUROPA</strong><span>2026</span></div>
        <small>V0.3 · Preflight offline</small>
      </header>

      <section className="offline-check-content">
        <p className="offline-eyebrow">PREPARACIÓN DEL DISPOSITIVO</p>
        <h1>Modo viaje</h1>
        <p className="offline-intro">Verifica que el itinerario, la aplicación y el paquete cifrado de boletos puedan abrirse aunque no exista conexión.</p>

        {!state?.standalone && (
          <div className="install-notice">
            <strong>Recomendación para iPhone</strong>
            <p>En Safari usa Compartir → Añadir a pantalla de inicio. La prueba definitiva debe hacerse desde ese icono, no desde una pestaña normal del navegador.</p>
          </div>
        )}

        <div className={`offline-score ${ready ? 'ready' : ''}`}>
          <span>{ready ? 'LISTO' : 'REVISAR'}</span>
          <strong>{ready ? 'Núcleo offline preparado' : 'Faltan comprobaciones'}</strong>
          <small>{state?.cacheName ?? 'Esperando Service Worker'}</small>
        </div>

        <div className="offline-status-card">
          <StatusRow label="Aplicación instalada" ok={state?.standalone ?? null} detail={state?.standalone ? 'Ejecutándose como PWA independiente' : 'Ahora está abierta en el navegador'} />
          <StatusRow label="Service Worker activo" ok={state?.serviceWorkerControlled ?? null} detail={state?.serviceWorkerControlled ? 'Esta pantalla está controlada por el modo offline' : 'Puede requerir cerrar y volver a abrir la app'} />
          <StatusRow label="Aplicación en caché" ok={state?.shellCached ?? null} detail={state?.shellCached ? `${state?.cachedEntries ?? 0} recursos locales preparados` : 'El shell todavía no está confirmado en caché'} />
          <StatusRow label="Wallet cifrado en caché" ok={state?.walletCached ?? null} detail={state?.walletCached ? 'El carrier cifrado está disponible sin red' : 'Debe descargarse al menos una vez con conexión'} />
          <StatusRow label="Almacenamiento persistente" ok={state?.persistentStorage ?? null} detail={state?.persistentStorage === true ? 'El navegador concedió persistencia' : state?.persistentStorage === false ? 'El navegador no garantiza persistencia; mantendremos redundancia' : 'No reportado por este navegador'} />
          <StatusRow label="Base para mapas offline" ok={state?.opfsSupported ?? null} detail={state?.opfsSupported ? 'OPFS disponible para futuros paquetes PMTiles' : 'Se utilizará fallback compatible si es necesario'} />
        </div>

        <div className="offline-storage">
          <span><small>Uso local</small><strong>{formatBytes(state?.usageBytes ?? null)}</strong></span>
          <span><small>Cuota estimada</small><strong>{formatBytes(state?.quotaBytes ?? null)}</strong></span>
        </div>

        <button type="button" className="offline-primary" disabled={working} onClick={() => void prepare()}>{working ? 'Preparando…' : 'Preparar este dispositivo'}</button>
        <button type="button" className="offline-secondary" onClick={() => void refresh()}>Volver a verificar</button>
        <a className="offline-back" href="/">‹ Volver a Europa 2026</a>
        <p className="offline-message" role="status">{message}</p>

        <div className="airplane-test">
          <strong>Prueba que haremos después</strong>
          <p>1. Abrir la app desde pantalla de inicio. 2. Activar modo avión. 3. Cerrar y volver a abrir. 4. Entrar a Viaje y Boletos. 5. Confirmar que todo sigue disponible.</p>
        </div>
      </section>
    </main>
  );
}
